import React, { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Clock, FileText,
  Loader2, Lock, Mail, MessageSquare, Plus, Send, Shield, Trash2, XCircle, Users, RefreshCw,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { ExtractObligationsButton } from "./ExtractObligationsButton";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { PremiumGate } from "./DecisionIntelligenceTab";
import { NoticeDetailDialog } from "./NoticeDetailDialog";

const NOTICE_TYPES = [
  { value: "breach", label: "Breach of Contract" },
  { value: "delay", label: "Delay Notice" },
  { value: "quality_failure", label: "Quality Failure" },
  { value: "cure", label: "Cure Notice" },
  { value: "warning", label: "Warning Notice" },
  { value: "termination", label: "Termination Notice" },
  { value: "general", label: "General Notice" },
];

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: FileText },
  in_review: { label: "In Review", color: "bg-amber-100 text-amber-700", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  sent: { label: "Sent", color: "bg-cyan-100 text-cyan-700", icon: Mail },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
};

const URGENCY_CONFIG = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-red-100 text-red-700",
};

export function NoticesTab({ contract, users: allUsers }) {
  const { user } = useAuth();
  const hasPremium = user?.premium_features?.includes("notices") || ["superuser", "admin"].includes(user?.role);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const fetchNotices = useCallback(async () => {
    if (!hasPremium) { setLoading(false); return; }
    try {
      const res = await apiClient.get(`/notices?contract_id=${contract.id}`);
      setNotices(res.data);
    } catch { /* ok */ }
    finally { setLoading(false); }
  }, [contract.id, hasPremium]);

  useEffect(() => { fetchNotices(); }, [fetchNotices]);

  const fetchSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const res = await apiClient.get(`/notices/suggestions/${contract.id}`);
      setSuggestions(res.data.suggestions || []);
      if (!res.data.suggestions?.length) toast.info("No notice suggestions at this time.");
    } catch { toast.error("Failed to fetch suggestions"); }
    finally { setSuggestionsLoading(false); }
  };

  const content = (
    <Card>
      <CardContent className="pt-6" data-testid="notices-tab">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">Contract Notices</h3>
            <Badge className="rounded-sm text-xs bg-slate-100 text-slate-600">{notices.length}</Badge>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchSuggestions} disabled={suggestionsLoading} className="gap-1.5 rounded-sm h-8 text-xs" data-testid="ai-suggestions-btn">
              {suggestionsLoading ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
              AI Suggestions
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5 rounded-sm h-8 text-xs" data-testid="create-notice-btn">
              <Plus size={14} /> Create Notice
            </Button>
          </div>
        </div>

        {/* AI Suggestions Banner */}
        {suggestions.length > 0 && (
          <div className="mb-4 p-3 border border-amber-200 bg-amber-50/50 rounded-sm space-y-2" data-testid="ai-suggestions-panel">
            <p className="text-xs font-mono uppercase text-amber-600 mb-1">AI Advisory Suggestions</p>
            {suggestions.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 border rounded-sm">
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-sm text-[10px] ${URGENCY_CONFIG[s.urgency] || URGENCY_CONFIG.medium}`}>{s.urgency}</Badge>
                  <span className="text-sm">{s.reason}</span>
                </div>
                <Button
                  size="sm" variant="outline" className="h-7 text-xs rounded-sm gap-1"
                  onClick={() => { setCreateOpen(true); setSuggestions(prev => prev.map((x, j) => j === i ? { ...x, _selected: true } : x)); }}
                  data-testid={`suggestion-act-${i}`}
                >
                  <Plus size={12} /> Create
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Notice List */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-slate-400" /></div>
        ) : notices.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Mail size={40} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">No notices for this contract</p>
            <p className="text-xs text-slate-400 mt-1">Create a notice or check AI suggestions</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notices.map((n) => (
              <NoticeRow key={n.id} notice={n} onClick={() => setSelectedNotice(n)} />
            ))}
          </div>
        )}

        {/* Create Notice Dialog */}
        <CreateNoticeDialog
          open={createOpen}
          onOpenChange={(v) => { setCreateOpen(v); if (!v) setSuggestions(s => s.map(x => ({ ...x, _selected: false }))); }}
          contract={contract}
          suggestion={suggestions.find(s => s._selected)}
          allUsers={allUsers}
          onCreated={fetchNotices}
        />

        {/* Notice Detail Dialog */}
        {selectedNotice && (
          <NoticeDetailDialog
            notice={selectedNotice}
            onClose={() => setSelectedNotice(null)}
            onRefresh={fetchNotices}
            allUsers={allUsers}
            currentUser={user}
            contract={contract}
          />
        )}
      </CardContent>
    </Card>
  );

  if (!hasPremium) {
    return (
      <div className="relative">
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm rounded-lg">
          <div className="text-center p-8 max-w-sm">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Premium Feature</h3>
            <p className="text-sm text-slate-500 mb-4">
              Contract Notices is a premium feature for AI-powered notice drafting, review workflows, and supplier communication.
            </p>
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-sm px-3 py-1 text-xs">
              Contact your administrator to enable access
            </Badge>
          </div>
        </div>
        <div className="opacity-20 pointer-events-none select-none">{content}</div>
      </div>
    );
  }

  return content;
}

function NoticeRow({ notice, onClick }) {
  const sc = STATUS_CONFIG[notice.status] || STATUS_CONFIG.draft;
  const urgency = notice.executive_summary?.urgency_level;

  return (
    <div
      className="flex items-center gap-3 p-3 border rounded-sm hover:border-slate-300 dark:hover:border-slate-600 cursor-pointer transition-colors"
      onClick={onClick}
      data-testid={`notice-row-${notice.id}`}
    >
      <div className="w-8 h-8 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
        {React.createElement(sc.icon, { size: 16, className: "text-slate-500" })}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium truncate">{notice.subject || `${notice.notice_type} notice`}</span>
          <Badge className={`rounded-sm text-[10px] ${sc.color}`}>{sc.label}</Badge>
          {urgency && <Badge className={`rounded-sm text-[10px] ${URGENCY_CONFIG[urgency]}`}>{urgency}</Badge>}
        </div>
        <p className="text-xs text-slate-500 truncate">{notice.issue_description}</p>
      </div>
      <span className="text-[10px] text-slate-400 whitespace-nowrap">
        {new Date(notice.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
      </span>
    </div>
  );
}

function CreateNoticeDialog({ open, onOpenChange, contract, suggestion, allUsers, onCreated }) {
  const [noticeType, setNoticeType] = useState(suggestion?.notice_type || "general");
  const [issueDescription, setIssueDescription] = useState(suggestion?.reason || "");
  const [creating, setCreating] = useState(false);
  const [obligations, setObligations] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [selectedObligations, setSelectedObligations] = useState([]);
  const [selectedMilestones, setSelectedMilestones] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (suggestion) {
      setNoticeType(suggestion.notice_type || "general");
      setIssueDescription(suggestion.reason || "");
    }
    // Fetch obligations/milestones for this contract
    apiClient.get(`/obligations?contract_id=${contract.id}`).then(r => setObligations(r.data)).catch(() => {});
    apiClient.get(`/milestones?contract_id=${contract.id}`).then(r => setMilestones(r.data)).catch(() => {});
  }, [open, contract.id, suggestion]);

  const handleCreate = async () => {
    if (!issueDescription.trim()) { toast.error("Please describe the issue"); return; }
    setCreating(true);
    try {
      await apiClient.post("/notices", {
        contract_id: contract.id,
        notice_type: noticeType,
        issue_description: issueDescription,
        linked_obligations: selectedObligations,
        linked_milestones: selectedMilestones,
      });
      toast.success("Notice drafted by AI. Review and edit before submitting.");
      onOpenChange(false);
      setIssueDescription("");
      setSelectedObligations([]);
      setSelectedMilestones([]);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create notice");
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby="create-notice-desc">
        <DialogHeader>
          <DialogTitle>Create Contract Notice</DialogTitle>
          <DialogDescription id="create-notice-desc">AI will draft a formal notice and executive summary based on your input.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Notice Type *</Label>
            <Select value={noticeType} onValueChange={setNoticeType}>
              <SelectTrigger className="rounded-sm" data-testid="notice-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NOTICE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Issue Description *</Label>
            <Textarea
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              placeholder="Describe the issue that warrants this notice (e.g., 'Supplier failed to deliver Phase 2 milestones by the agreed deadline of March 15')"
              rows={4}
              className="rounded-sm"
              data-testid="issue-description-input"
            />
          </div>
          {obligations.length > 0 && (
            <div className="space-y-2">
              <Label>Link Obligations</Label>
              <div className="max-h-24 overflow-auto border rounded-sm p-2 space-y-1">
                {obligations.map(o => (
                  <label key={o.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedObligations.includes(o.id)}
                      onChange={(e) => setSelectedObligations(prev => e.target.checked ? [...prev, o.id] : prev.filter(x => x !== o.id))}
                      className="rounded"
                    />
                    <span className="truncate">{o.title}</span>
                    <Badge className={`rounded-sm text-[9px] ml-auto ${o.compliance_status === 'non_compliant' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>{o.compliance_status}</Badge>
                  </label>
                ))}
              </div>
            </div>
          )}
          {milestones.length > 0 && (
            <div className="space-y-2">
              <Label>Link Milestones</Label>
              <div className="max-h-24 overflow-auto border rounded-sm p-2 space-y-1">
                {milestones.map(m => (
                  <label key={m.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedMilestones.includes(m.id)}
                      onChange={(e) => setSelectedMilestones(prev => e.target.checked ? [...prev, m.id] : prev.filter(x => x !== m.id))}
                      className="rounded"
                    />
                    <span className="truncate">{m.title}</span>
                    <Badge className={`rounded-sm text-[9px] ml-auto ${m.status === 'missed' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>{m.status}</Badge>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleCreate} disabled={creating} className="gap-2 rounded-sm" data-testid="generate-notice-btn">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            {creating ? "AI Generating..." : "Generate Notice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
